import {
  NOTIFICATION_INBOX_PAGE_SIZE,
  notificationActionResponseSchema,
  notificationDeleteAllResponseSchema,
  notificationListResponseSchema,
  notificationPreferencesResponseSchema,
  notificationStandardResponseSchema,
  type NotificationProductCode,
  type UpdateNotificationPreferencesRequest,
  type UpdateNotificationStandardRequest,
} from '@gb-ticket/shared-types';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiFetch } from '@/lib/api';
import { useRealtimeConnected } from '@/platform/lib/realtime-connection';

/** Prefix for every notification query — invalidating it refreshes bell, inbox and prefs. */
export const notificationsQueryKey = ['notifications'] as const;
export const notificationPreferencesQueryKey = ['notifications', 'preferences'] as const;
export const notificationStandardQueryKey = ['notifications', 'standard'] as const;

const BELL_LIST_LIMIT = 30;

export type NotificationInboxFilter = {
  unreadOnly: boolean;
  productCode: NotificationProductCode | null;
  page: number;
};

export function useNotifications(enabled: boolean) {
  const socketUp = useRealtimeConnected();
  return useQuery({
    queryKey: notificationsQueryKey,
    queryFn: () =>
      apiFetch(`/notifications?limit=${BELL_LIST_LIMIT}`, notificationListResponseSchema),
    enabled,
    refetchOnWindowFocus: false,
    refetchInterval: socketUp ? false : 60_000,
  });
}

export function useNotificationInbox(filter: NotificationInboxFilter) {
  return useQuery({
    queryKey: [...notificationsQueryKey, 'inbox', filter] as const,
    queryFn: () => {
      const params = new URLSearchParams({
        page: String(filter.page),
        pageSize: String(NOTIFICATION_INBOX_PAGE_SIZE),
      });
      if (filter.unreadOnly) {
        params.set('unread', 'true');
      }
      if (filter.productCode) {
        params.set('productCode', filter.productCode);
      }
      return apiFetch(`/notifications?${params.toString()}`, notificationListResponseSchema);
    },
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: true,
  });
}

export function useNotificationPreferences(enabled: boolean) {
  return useQuery({
    queryKey: notificationPreferencesQueryKey,
    queryFn: () => apiFetch('/notifications/preferences', notificationPreferencesResponseSchema),
    enabled,
  });
}

export function useMarkNotificationRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/notifications/${id}/read`, notificationActionResponseSchema, { method: 'POST' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: notificationsQueryKey });
    },
  });
}

export function useMarkAllNotificationsRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch('/notifications/read-all', notificationActionResponseSchema, { method: 'POST' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: notificationsQueryKey });
    },
  });
}

export function useDeleteNotification() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/notifications/${encodeURIComponent(id)}`, notificationActionResponseSchema, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: notificationsQueryKey });
    },
  });
}

export function useDeleteAllNotifications() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch('/notifications', notificationDeleteAllResponseSchema, { method: 'DELETE' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: notificationsQueryKey });
    },
  });
}

export function useUpdateNotificationPreferences() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateNotificationPreferencesRequest) =>
      apiFetch('/notifications/preferences', notificationPreferencesResponseSchema, {
        method: 'PUT',
        body: JSON.stringify(body),
      }),
    onSuccess: (data) => {
      queryClient.setQueryData(notificationPreferencesQueryKey, data);
    },
  });
}

export function useRestoreNotificationPreferences() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch('/notifications/preferences', notificationPreferencesResponseSchema, {
        method: 'DELETE',
      }),
    onSuccess: (data) => {
      queryClient.setQueryData(notificationPreferencesQueryKey, data);
    },
  });
}

export function useNotificationStandard() {
  return useQuery({
    queryKey: notificationStandardQueryKey,
    queryFn: () => apiFetch('/notifications/standard', notificationStandardResponseSchema),
  });
}

export function useReplaceNotificationStandard() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateNotificationStandardRequest) =>
      apiFetch('/notifications/standard', notificationStandardResponseSchema, {
        method: 'PUT',
        body: JSON.stringify(body),
      }),
    onSuccess: (data) => {
      queryClient.setQueryData(notificationStandardQueryKey, data);
    },
  });
}

export function useClearNotificationStandard() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch('/notifications/standard', notificationStandardResponseSchema, {
        method: 'DELETE',
      }),
    onSuccess: (data) => {
      queryClient.setQueryData(notificationStandardQueryKey, data);
    },
  });
}
